import * as React from 'react'
import { connect } from 'react-redux'
import {
    CardBody,
    Row,
    CardHeader,
    Col,
    Collapse,
    Input,
    Label
} from "reactstrap";
import {WithTranslation, withTranslation} from "react-i18next";
import {CurrencyProps, DetailPriceProps, Language, NATURAL_SORT_COMPARE_OPTIONS} from "App/constants";
import {extractPrice} from "App/services";
import {
    ConfiguratorProductCategories,
    ConfiguratorProductOptions, ConfiguratorProductTypes
} from "App/services/api";
import {basketOptionSelectionChange} from "App/basket";
import {ReadMore} from "App/fragments/ReadMore";
import {List as ImmutableList, Map as ImmutableMap} from "immutable";
import {selectedLang} from "App/helper";
import * as ReactMarkdown from "react-markdown";

interface StateToProps extends CurrencyProps, DetailPriceProps {
    categories: Array<ConfiguratorProductCategories>
    incompatibleOptions: Array<number>
    options: ImmutableMap<number,ImmutableList<number>>
    chosenType: ConfiguratorProductTypes | undefined;
}

interface DispatchToProps {
    onOptionSelection: (selected: boolean, category: ConfiguratorProductCategories, option: ConfiguratorProductOptions) => void
}

const mapDispatchToProps = (dispatch: any) : DispatchToProps => ({
    onOptionSelection: (selected: boolean, category: ConfiguratorProductCategories, option: ConfiguratorProductOptions) => dispatch(basketOptionSelectionChange(selected, category, option)),
});

interface ComponentState {
    optionVisibility: ImmutableMap<number,boolean>
}

function initialState (props: StateToProps): ComponentState {
    const state = {
        optionVisibility: ImmutableMap<number,boolean>(),
    };
    if (props.categories) {
        props.categories.map((category, index) => (
            state.optionVisibility = state.optionVisibility.set(category.categoryid, props.options.has(category.categoryid))
        ));
    }
    return state;
}

class _Component extends React.PureComponent<StateToProps & DispatchToProps & WithTranslation, ComponentState> {

    constructor(props: StateToProps & DispatchToProps & WithTranslation, context: any) {
        super(props, context);
        this.state = initialState(props);
    }

    toggleVisibility(id: number) {
        this.setState((state, props) => ({
            optionVisibility: state.optionVisibility.set(id, !state.optionVisibility.get(id))
        }));
    }

    render() {
        const {i18n, currency, detailPrice, categories, t, onOptionSelection, options, incompatibleOptions, chosenType} = this.props;
        const language: Language = selectedLang(i18n);
        return categories
            .sort((c1:ConfiguratorProductCategories, c2:ConfiguratorProductCategories) => c1.position - c2.position)
            .map( (category: ConfiguratorProductCategories, index: number) => {
                const categoryOptions: ImmutableList<number> = options.get(category.categoryid)||ImmutableList();
                const filteredOptions: Array<ConfiguratorProductOptions> = category.options&&category.options
                    .filter( (option: ConfiguratorProductOptions) => incompatibleOptions.indexOf(option.optionid)<0);
                return filteredOptions && filteredOptions.length ?
                    <React.Fragment key={category.categoryid}>
                        <CardHeader className="px-5 d-flex align-items-center py-3 bg-white toggable"
                                    onClick={(e) => this.toggleVisibility(category.categoryid)}>
                            <span className="m-0 w-75 float-left title-normal-uppercase">{category.nameml[language]}</span>
                            <i className={'far fa-lg w-25 text-right ' + (this.state.optionVisibility.get(category.categoryid) ? 'fa-chevron-up' : 'fa-chevron-down')}></i>
                        </CardHeader>
                        <Collapse isOpen={this.state.optionVisibility.get(category.categoryid)}>
                            <CardBody className="px-5 pt-2 pb-3 bodyslice">
                                {
                                    category.defaultdescriptionsml&&chosenType&&category.defaultdescriptionsml[chosenType.typeid]?
                                        <Row key={'defaultdescription'} className="no-gutters">
                                            <Col className="align-items-center py-2 pl-0">
                                                <ReactMarkdown className="text-normal-details align-middle"
                                                               source={(category.defaultdescriptionsml[chosenType.typeid][language]||'').replace(/(?:\r|\n|\\n|\\r)/g, ' ')}
                                                               escapeHtml={false} />
                                            </Col>
                                        </Row>:
                                        null
                                }
                                {
                                    filteredOptions
                                        .sort((i1:ConfiguratorProductOptions, i2:ConfiguratorProductOptions) => (i1.titleml[language]||'').localeCompare(i2.titleml[language]||'', undefined, NATURAL_SORT_COMPARE_OPTIONS))
                                        .map((option: ConfiguratorProductOptions, index: number) => {
                                            const checked = categoryOptions.indexOf(option.optionid)>=0;
                                            const price: number | null = extractPrice(option.price, currency);
                                            return <Row key={option.optionid} className="no-gutters">
                                                <Col className={"align-items-center py-2 pr-0 " + detailPrice?"col-sm-9":"col"}>
                                                    <div className="form-check">
                                                        <Input
                                                            type={category.singleselection ? "radio" : "checkbox"}
                                                            checked={checked}
                                                            id={"optionInput" + option.optionid}
                                                            name={"optionInput" + option.optionid}
                                                            className="align-middle" onChange={(e) => onOptionSelection(e.target.checked, category, option)}
                                                            onClick={(e) => category.singleselection&&checked&&onOptionSelection(false, category, option)}/>
                                                        <Label for={"optionInput" + option.optionid}
                                                               className="form-check-label text-normal-details light align-middle">{option.titleml[language]}</Label>
                                                        <ReadMore>
                                                            <ReactMarkdown className="w-100 text-small-details align-middle light"
                                                                           source={(option.descriptionml[language]||'').replace(/(?:\r|\n|\\n|\\r)/g, '<br>')}
                                                                           escapeHtml={false} />
                                                        </ReadMore>
                                                    </div>
                                                </Col>
                                                {
                                                    detailPrice ?
                                                        <Col
                                                            className="align-items-center py-2 pl-0 col-sm-3 text-right">
                                                            <Label
                                                                className={"text-normal-details uppercase align-middle " + (checked ? "" : "lighter")}>{price ?
                                                                t('products.configuration.price', {
                                                                    amount: price,
                                                                    unit: currency
                                                                })
                                                                : t('products.configuration.pricenotavailable')
                                                            }</Label>
                                                        </Col> :
                                                        null
                                                }
                                            </Row>
                                    })
                                }
                            </CardBody>
                        </Collapse>
                    </React.Fragment>
                    : null
            }
        )
    }
}

export const Component = connect(null, mapDispatchToProps)(withTranslation('common')(_Component));


// WEBPACK FOOTER //
// ./src/components/OptionsConf.tsx